import React, { useEffect, useMemo, useState } from "react";
import { Copy, PlusCircle, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";
import { copyToClipboard, formatDateTime, getStatusLabel, getStatusTone } from "../lib/utils";

const initialForm = {
  child_id: "",
  driver_id: "",
  vehicle_id: "",
  scheduled_start_time: "",
  scheduled_end_time: "",
  notes: "",
};

export default function TripsPage() {
  const { user } = useAuth();
  const {
    trips,
    childrenRecords,
    vehicles,
    users,
    loadTrips,
    loadChildren,
    loadVehicles,
    loadUsers,
    createTrip,
    updateTrip,
    confirmArrival,
  } = useData();

  const [form, setForm] = useState(initialForm);
  const [editingTrip, setEditingTrip] = useState(null);

  useEffect(() => {
    const hydrate = async () => {
      await loadTrips();
      if (user?.role === "admin") {
        await Promise.all([loadChildren(), loadVehicles(), loadUsers("driver")]);
      }
    };
    hydrate();
  }, [user, loadTrips, loadChildren, loadVehicles, loadUsers]);

  const driverOptions = useMemo(() => users.filter((entry) => entry.role === "driver"), [users]);

  const resetForm = () => {
    setForm(initialForm);
    setEditingTrip(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.child_id) {
      toast.error("Select a child before saving the trip.");
      return;
    }

    const payload = {
      ...form,
      driver_id: form.driver_id || null,
      vehicle_id: form.vehicle_id || null,
      scheduled_start_time: form.scheduled_start_time || null,
      scheduled_end_time: form.scheduled_end_time || null,
    };

    const result = editingTrip ? await updateTrip(editingTrip.id, payload) : await createTrip(payload);
    if (result.success) {
      toast.success(editingTrip ? "Trip updated." : "Trip created.");
      resetForm();
      loadTrips();
    } else {
      toast.error(result.error);
    }
  };

  const handleEdit = (trip) => {
    setEditingTrip(trip);
    setForm({
      child_id: trip.child_id || "",
      driver_id: trip.driver_id || "",
      vehicle_id: trip.vehicle_id || "",
      scheduled_start_time: trip.scheduled_start_time ? trip.scheduled_start_time.slice(0, 16) : "",
      scheduled_end_time: trip.scheduled_end_time ? trip.scheduled_end_time.slice(0, 16) : "",
      notes: trip.notes || "",
    });
  };

  const handleConfirmArrival = async (tripId) => {
    const result = await confirmArrival(tripId);
    if (result.success) {
      toast.success("Arrival confirmed.");
      loadTrips();
    } else {
      toast.error(result.error);
    }
  };

  const copyShareLink = async (trip) => {
    const copied = await copyToClipboard(`${window.location.origin}/track/${trip.tracking_token}`);
    if (copied) toast.success("Tracking link copied.");
    else toast.error("Unable to copy the tracking link.");
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {user?.role === "admin" && (
        <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
          <form onSubmit={handleSubmit} className="guardian-card bg-white p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">{editingTrip ? "Edit trip" : "Create trip"}</h2>
                <p className="mt-1 text-sm text-[#64748B]">Assign the child, driver, vehicle, and schedule.</p>
              </div>
              {editingTrip ? (
                <button type="button" onClick={resetForm} className="text-sm font-medium text-[#163D6B]">
                  Cancel edit
                </button>
              ) : null}
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-[#0F172A]">Child</label>
                <select value={form.child_id} onChange={(event) => setForm((current) => ({ ...current, child_id: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-3 py-3 text-sm">
                  <option value="">Select child</option>
                  {childrenRecords.map((child) => (
                    <option key={child.id} value={child.id}>{child.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#0F172A]">Driver</label>
                <select value={form.driver_id} onChange={(event) => setForm((current) => ({ ...current, driver_id: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-3 py-3 text-sm">
                  <option value="">Assign later</option>
                  {driverOptions.map((driver) => (
                    <option key={driver.id} value={driver.id}>{driver.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#0F172A]">Vehicle</label>
                <select value={form.vehicle_id} onChange={(event) => setForm((current) => ({ ...current, vehicle_id: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-3 py-3 text-sm">
                  <option value="">Assign later</option>
                  {vehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>{vehicle.registration}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#0F172A]">Start time</label>
                <input type="datetime-local" value={form.scheduled_start_time} onChange={(event) => setForm((current) => ({ ...current, scheduled_start_time: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-3 py-3 text-sm" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#0F172A]">End time</label>
                <input type="datetime-local" value={form.scheduled_end_time} onChange={(event) => setForm((current) => ({ ...current, scheduled_end_time: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-3 py-3 text-sm" />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium text-[#0F172A]">Notes</label>
              <textarea value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} className="mt-2 min-h-[120px] w-full rounded-xl border border-[#E2E8F0] bg-white px-3 py-3 text-sm" />
            </div>
            <button type="submit" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#163D6B] px-4 py-3 text-sm font-semibold text-white">
              {editingTrip ? <Save size={16} /> : <PlusCircle size={16} />}
              {editingTrip ? "Save changes" : "Create trip"}
            </button>
          </form>

          <div className="guardian-card bg-white p-6">
            <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Assignment notes</h2>
            <div className="mt-4 space-y-4 text-sm text-[#475569]">
              <p>Trips store role-based visibility automatically. Parents only see linked children, schools only see linked pupils, and drivers only see assigned trips.</p>
              <p>GPS tracking starts when a driver begins a trip and stops when the trip is completed.</p>
              <p>Tracking links are token-based and can be shared with trusted contacts from the parent dashboard or trips list.</p>
            </div>
          </div>
        </div>
      )}

      <div className="guardian-card overflow-hidden bg-white">
        <div className="border-b border-[#E2E8F0] px-6 py-4">
          <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Trip list</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[#64748B]">
              <tr>
                <th className="px-6 py-3 font-medium">Child</th>
                <th className="px-6 py-3 font-medium">Driver</th>
                <th className="px-6 py-3 font-medium">Vehicle</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">ETA</th>
                <th className="px-6 py-3 font-medium">Updated</th>
                <th className="px-6 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {trips.length ? (
                trips.map((trip) => (
                  <tr key={trip.id} className="border-t border-[#E2E8F0] align-top">
                    <td className="px-6 py-4 font-medium text-[#0F172A]">
                      <div>{trip.child_name}</div>
                      <div className="mt-1 text-xs text-[#64748B]">{trip.pickup_location}</div>
                    </td>
                    <td className="px-6 py-4 text-[#475569]">{trip.driver_name || "Unassigned"}</td>
                    <td className="px-6 py-4 text-[#475569]">{trip.vehicle_registration || "Unassigned"}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getStatusTone(trip.status)}`}>
                        {getStatusLabel(trip.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-[#475569]">{trip.eta_minutes ? `${trip.eta_minutes} min` : "Pending"}</td>
                    <td className="px-6 py-4 text-[#475569]">{formatDateTime(trip.last_location?.timestamp || trip.updated_at)}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        {user?.role === "admin" && (
                          <button onClick={() => handleEdit(trip)} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs font-medium text-[#0F172A]">
                            Edit
                          </button>
                        )}
                        {user?.role === "school" && trip.status === "completed" && !trip.school_arrival_confirmed_at && (
                          <button onClick={() => handleConfirmArrival(trip.id)} className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white">
                            Confirm arrival
                          </button>
                        )}
                        {user?.role === "parent" && trip.tracking_token && (
                          <button onClick={() => copyShareLink(trip)} className="inline-flex items-center gap-1 rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs font-medium text-[#0F172A]">
                            <Copy size={12} />
                            Copy link
                          </button>
                        )}
                        {user?.role === "driver" && (
                          <span className="text-xs text-[#64748B]">Use the driver dashboard to run checklists and live GPS.</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-[#64748B]">No trips found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
